import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import getUserSessions from '../apis/getUserSessions'

const AuthContext = createContext({})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('isAuthenticated')
  })

  const [user, setUser] = useState(() => {
    const localUser = localStorage.getItem('admin')
    return localUser ? JSON.parse(localUser) : null
  })

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('isAuthenticated', 'true')
      localStorage.setItem('admin', JSON.stringify(user))
    } else {
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('admin')
    }
  }, [isAuthenticated, user])

  const logout = useCallback(() => {
    setUser(null)
    setIsAuthenticated(false)
  }, [])

  useEffect(() => {
    const verifySession = async () => {
      try {
        const sessions = await getUserSessions()
        if (!sessions) {
          logout()
        }
      } catch (error) {
        console.error('Failed to fetch admin sessions:', error)
        logout()
      }
    }

    if (isAuthenticated) {
      verifySession()
    }
  }, [isAuthenticated, logout])

  const login = (userData) => {
    setUser(userData)
    setIsAuthenticated(true)
    const sessionStartTime = Date.now()

    localStorage.setItem('sessionStartTime', sessionStartTime)
  }

  useEffect(() => {
    const checkSessionValidity = () => {
      const sessionStartTime = localStorage.getItem('sessionStartTime')
      const sessionDuration =
        localStorage.getItem('sessionDuration') || 60 * 60 * 1000

      if (sessionStartTime && sessionDuration) {
        const currentTime = Date.now()
        const sessionExpirationTime =
          parseInt(sessionStartTime, 10) + parseInt(sessionDuration, 10)

        if (currentTime > sessionExpirationTime) {
          logout()
        }
      }
    }

    if (isAuthenticated) {
      checkSessionValidity()

      // Optionally, you could also check session validity at intervals
      const intervalId = setInterval(checkSessionValidity, 60000) // check every minute
      return () => clearInterval(intervalId)
    }
  }, [isAuthenticated, logout])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
